import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';
import { useAppStore } from '../../store/appStore';
import api from '../../services/api';

const getToday = () => {
  const date = new Date();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
};

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return `${date.toLocaleDateString('en-IN')} ${date.toLocaleTimeString('en-IN', {
    hour: '2-digit',
    minute: '2-digit',
  })}`;
};

const formatAmount = (value) => {
  const amount = Number(value);
  return Number.isFinite(amount) ? `₹${amount.toFixed(2)}` : '-';
};

const getOrderTypeLabel = (orderType) => ({
  dine_in: 'Dine In',
  pickup: 'Pick Up',
  delivery: 'Delivery',
}[orderType] || orderType || '-');

const ComplimentaryBills = () => {
  const navigate = useNavigate();
  const { branch: selectedBranch } = useAppStore();
  const today = getToday();
  const [draftFromDate, setDraftFromDate] = useState(today);
  const [draftToDate, setDraftToDate] = useState(today);
  const [appliedDates, setAppliedDates] = useState({ fromDate: today, toDate: today });
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const branchId = selectedBranch?.isAllOutlets || selectedBranch?.id === 'all-outlets'
    ? 'all'
    : selectedBranch?.id || 'all';
  const isAllOutlets = branchId === 'all';

  useEffect(() => {
    let isCurrentRequest = true;

    const fetchBills = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await api.get('/vendors/complimentary-bills/', {
          params: {
            from_date: appliedDates.fromDate,
            to_date: appliedDates.toDate,
            branch_id: branchId,
          },
        });
        const results = response?.data?.data?.results;
        if (!response?.data?.success || !Array.isArray(results)) {
          throw new Error(response?.data?.message || 'Unable to load complimentary bills.');
        }
        if (isCurrentRequest) setBills(results);
      } catch (requestError) {
        if (isCurrentRequest) {
          setBills([]);
          setError(requestError.response?.data?.message || requestError.message || 'Unable to load complimentary bills.');
        }
      } finally {
        if (isCurrentRequest) setLoading(false);
      }
    };

    fetchBills();
    return () => {
      isCurrentRequest = false;
    };
  }, [appliedDates, branchId]);

  const handleApply = () => {
    if (!draftFromDate || !draftToDate) {
      setError('Please select both dates.');
      return;
    }
    if (draftFromDate > draftToDate) {
      setError('From Date cannot be after To Date.');
      return;
    }
    setError('');
    setAppliedDates({ fromDate: draftFromDate, toDate: draftToDate });
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 2.5, md: 3 }, minHeight: '100%', backgroundColor: 'background.default' }}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, md: 3 },
          mb: 2.5,
          borderRadius: 4,
          border: '1px solid',
          borderColor: 'divider',
          background: 'linear-gradient(180deg, rgba(255,255,255,1), rgba(248,250,252,1))',
          boxShadow: '0 12px 24px rgba(15, 23, 42, 0.04)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate('/vendor/dashboard')}>
            Back
          </Button>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 700, flexGrow: 1 }}>
            Complimentary Bills
          </Typography>
          <TextField
            size="small"
            label="From Date"
            type="date"
            value={draftFromDate}
            onChange={(event) => setDraftFromDate(event.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            size="small"
            label="To Date"
            type="date"
            value={draftToDate}
            onChange={(event) => setDraftToDate(event.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <Button variant="contained" onClick={handleApply} disabled={loading}>
            Apply
          </Button>
        </Box>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : null}

      {!loading && !error && bills.length === 0 ? (
        <Paper elevation={0} sx={{ p: 5, textAlign: 'center', borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
          <Typography color="text.secondary">
            {isAllOutlets
              ? 'No complimentary bills found for the selected date range.'
              : `No complimentary bills found for ${selectedBranch?.name || 'the selected branch'}.`}
          </Typography>
        </Paper>
      ) : null}

      {!loading && !error && bills.length > 0 ? (
        <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', overflow: 'auto' }}>
          <Table sx={{ minWidth: isAllOutlets ? 760 : 660 }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'action.hover' }}>
                <TableCell sx={{ fontWeight: 700 }}>Bill / Order No.</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Date / Time</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Order Type</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Table / Token</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Amount</TableCell>
                {isAllOutlets ? <TableCell sx={{ fontWeight: 700 }}>Branch</TableCell> : null}
              </TableRow>
            </TableHead>
            <TableBody>
              {bills.map((bill) => {
                const tableLabel = bill.table?.display_number || bill.table?.table_number;
                const tableOrToken = tableLabel ? `Table ${tableLabel}` : bill.token_no ? `Token ${bill.token_no}` : '-';
                return (
                  <TableRow key={bill.id} hover>
                    <TableCell>{bill.bill_no || bill.order_number || '-'}</TableCell>
                    <TableCell>{formatDateTime(bill.created_at)}</TableCell>
                    <TableCell>{getOrderTypeLabel(bill.order_type)}</TableCell>
                    <TableCell>{tableOrToken}</TableCell>
                    <TableCell align="right">{formatAmount(bill.total_amount)}</TableCell>
                    {isAllOutlets ? <TableCell>{bill.branch_name || '-'}</TableCell> : null}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      ) : null}
    </Box>
  );
};

export { ComplimentaryBills };
